/**
 * PlatformAccount — the Super Admin's own account page inside the Platform
 * Console. Currently: self-service password change.
 */
import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { KeyRound, Loader2, Eye, EyeOff, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function formatError(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || String(x)).join(", ");
  return e?.message || "Something went wrong";
}

export default function PlatformAccount() {
  const { user } = useAuth();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async () => {
    if (next.length < 12) return toast.error("New password must be at least 12 characters");
    if (next !== confirm) return toast.error("Passwords do not match");
    if (next === current) return toast.error("New password must differ from the current one");
    setBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password changed. Other sessions have been signed out.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="platform-account-page">
      <header>
        <div className="text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold">
          Platform
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1">My Account</h1>
        <p className="text-slate-600 mt-2 text-sm">
          Manage your Super Admin credentials.
        </p>
      </header>

      <div className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 border border-amber-200 flex items-center justify-center shrink-0">
            <ShieldCheck className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <div className="text-slate-900 font-medium">{user?.name}</div>
            <div className="text-[12px] text-slate-500 mono">{user?.email}</div>
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-white border border-slate-200 p-6 max-w-3xl shadow-sm">
        <div className="flex items-center gap-2 mb-1">
          <KeyRound className="w-4 h-4 text-slate-500" />
          <div className="text-[11px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
            Password
          </div>
        </div>
        <div className="text-sm text-slate-700 mb-4">
          Change your password. All your other active sessions will be signed out.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Current</Label>
            <Input
              type={show ? "text" : "password"}
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="bg-white border-slate-300 mt-1.5 mono"
              data-testid="account-pwd-current"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">New (≥ 12)</Label>
            <Input
              type={show ? "text" : "password"}
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="bg-white border-slate-300 mt-1.5 mono"
              data-testid="account-pwd-new"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-500">Confirm</Label>
            <Input
              type={show ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="bg-white border-slate-300 mt-1.5 mono"
              data-testid="account-pwd-confirm"
            />
          </div>
        </div>
        <div className="flex items-center gap-3 mt-4">
          <Button
            onClick={submit}
            disabled={busy || !current || !next || !confirm}
            data-testid="account-pwd-submit"
            className="bg-amber-500 text-white hover:bg-amber-600 font-semibold shadow-sm"
          >
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}
            Change password
          </Button>
          <button
            type="button"
            onClick={() => setShow((v) => !v)}
            className="text-[12px] text-slate-500 hover:text-slate-800 flex items-center gap-1"
            data-testid="account-pwd-toggle"
          >
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {show ? "Hide" : "Show"}
          </button>
        </div>
      </div>
    </div>
  );
}
